/**
 * Homepage search cache.
 * Survives React Router navigations (e.g. home → worker → Back) in the same tab.
 * Cleared on hard reload.
 */

export type HomeFilterUiState = {
  serviceId?: string;
  /** Select option values: `${regionId}:${municipalityName}` */
  municipalities?: string[];
  minHourlyPay?: number | null;
  maxHourlyPay?: number | null;
  minRating?: number | null;
  page?: number;
};

export type HomeSearchSnapshot = {
  apiPayload: Record<string, unknown>;
  ui: HomeFilterUiState;
  page: number;
  /** Cached listing so Back can restore without an immediate refetch flash */
  workers?: unknown;
};

const SESSION_KEY = "jandi:homeSearch";

let memoryCache: HomeSearchSnapshot | null = null;

/** Build API filter body from UI-oriented filter fields (same shape as HomeFilter). */
export function buildHomeApiPayload(ui: HomeFilterUiState): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (ui.serviceId) {
    payload.services = [ui.serviceId];
  }

  if (ui.municipalities && ui.municipalities.length > 0) {
    payload.regions = ui.municipalities
      .map((value) => value.split(":")[0])
      .filter(Boolean);
  }

  if (ui.minHourlyPay != null) payload.minHourlyPay = ui.minHourlyPay;
  if (ui.maxHourlyPay != null) payload.maxHourlyPay = ui.maxHourlyPay;
  if (ui.minRating != null) payload.minRating = ui.minRating;

  return payload;
}

export function uiStateFromFilterPayload(
  payload: Record<string, any>,
  municipalityValues?: string[]
): HomeFilterUiState {
  return {
    serviceId: Array.isArray(payload?.services) ? payload.services[0] : undefined,
    municipalities: municipalityValues,
    minHourlyPay: payload?.minHourlyPay ?? null,
    maxHourlyPay: payload?.maxHourlyPay ?? null,
    minRating: payload?.minRating ?? null,
  };
}

export function rememberHomeSearch(snapshot: HomeSearchSnapshot): void {
  const hasFilters = Object.keys(snapshot.apiPayload || {}).length > 0;
  if (!hasFilters) {
    clearHomeSearch();
    return;
  }

  memoryCache = {
    apiPayload: snapshot.apiPayload,
    ui: { ...snapshot.ui, page: snapshot.page },
    page: snapshot.page || 1,
    // Keep prior listing if this save is filters-only (e.g. right after clicking Search)
    workers:
      snapshot.workers !== undefined ? snapshot.workers : memoryCache?.workers,
  };

  if (typeof window === "undefined") return;
  try {
    // Persist UI/filters only (listing can be large); enough to restore after full reload of document via Back
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        apiPayload: memoryCache.apiPayload,
        ui: memoryCache.ui,
        page: memoryCache.page,
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

export function readHomeSearch(): HomeSearchSnapshot | null {
  if (memoryCache && Object.keys(memoryCache.apiPayload || {}).length > 0) {
    return memoryCache;
  }

  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeSearchSnapshot;
    if (!parsed?.apiPayload || Object.keys(parsed.apiPayload).length === 0) {
      return null;
    }
    memoryCache = {
      apiPayload: parsed.apiPayload,
      ui: parsed.ui || {},
      page: parsed.page || 1,
    };
    return memoryCache;
  } catch {
    return null;
  }
}

export function clearHomeSearch(): void {
  memoryCache = null;
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export function isHomeReload(): boolean {
  if (typeof performance === "undefined") return false;
  const entry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  return entry?.type === "reload";
}

/**
 * Read cached home search for a Home mount.
 * Clears cache only once when this document was loaded via hard reload —
 * not again on SPA Back remounts (nav type stays "reload" for the whole document).
 */
let reloadCacheHandled = false;

export function readHomeSearchForMount(): HomeSearchSnapshot | null {
  if (typeof window === "undefined") return null;

  if (!reloadCacheHandled) {
    reloadCacheHandled = true;
    if (isHomeReload()) {
      clearHomeSearch();
      return null;
    }
  }

  return readHomeSearch();
}
